/**
 * Reads the responsive contract off an image Liquid already rendered, so images rebuilt in JavaScript
 * keep the same candidate ladder, sizes contract and intrinsic dimensions instead of inventing their own.
 *
 * @param {Element | null | undefined} image
 * @param {number[]} fallbackWidths
 */
function readImageTemplate(image, fallbackWidths) {
  if (!(image instanceof HTMLImageElement)) {
    return { widths: fallbackWidths, sizes: null, width: null, height: null };
  }

  const widths = [...image.srcset.matchAll(/(\d+)w/g)].map((match) => Number(match[1]));

  return {
    widths: widths.length ? widths : fallbackWidths,
    sizes: image.getAttribute("sizes"),
    width: image.getAttribute("width"),
    height: image.getAttribute("height"),
  };
}

/**
 * Escapes a string for safe interpolation inside an HTML attribute value.
 *
 * @param {string} value
 */
function escapeAttr(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

if (!customElements.get("quick-add-custom-component")) {
  class QuickAddCustomComponent extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.selectedOptions = {};
      this.form = null;
      this.variantInput = null;
      this.submitBtn = null;
      this.btnText = null;
      this.feedback = null;
      this.feedbackText = null;
      this._siblingCache = new Map();
      this._fetchToken = 0;
      this._siblingsRequested = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const variantsJson = this.querySelector("[data-variants-json]");
      if (variantsJson) {
        try {
          this.variants = JSON.parse(variantsJson.textContent);
          this._originalVariants = [...this.variants];
        } catch (e) {
          console.error("Failed to parse variants JSON:", e);
        }
      }

      this._originalProductId = this.dataset.productId;

      this.form = this.querySelector('[data-type="add-to-cart-form"]');
      this.variantInput = this.querySelector("[data-variant-input]");
      this.submitBtn = this.querySelector("[data-add-to-cart-btn]");
      this.btnText = this.querySelector("[data-btn-text]");
      this.feedback = this.querySelector("[data-feedback]");
      this.feedbackText = this.querySelector("[data-feedback-text]");

      this._originalSizesHTML =
        this.querySelector(".quick-add-custom__sizes")?.innerHTML ?? null;

      const cardGallery = this.closest(".card-gallery");
      if (cardGallery) {
        this._mode = "slideshow";
        this._slideshow =
          cardGallery.querySelector("slideshow-component") ?? null;
        this._slideshowScroller =
          this._slideshow?.querySelector('[ref="scroller"]') ?? null;
        this._originalScrollerHTML = this._slideshowScroller?.innerHTML ?? null;
        // Mirror the responsive contract Liquid rendered for this card, so colour switching does not
        // silently swap right-sized candidates for a hardcoded, much larger ladder.
        this._slideImageTemplate = readImageTemplate(
          this._slideshowScroller?.querySelector("img"),
          [400, 800, 1200],
        );
        this._cardImg = null;
        this._cardLink =
          cardGallery.querySelector('[ref="cardGalleryLink"]') ?? null;
        this._originalHref = this._cardLink?.href ?? null;
      } else {
        this._mode = "simple";
        this._cardImg =
          this.closest(".product-card-simple__media")?.querySelector(
            ".product-card-simple__image",
          ) ?? null;
        this._originalImageSrc = this._cardImg?.src ?? null;
        this._originalImageSrcset = this._cardImg?.srcset ?? null;
        this._cardImageTemplate = readImageTemplate(this._cardImg, [200, 300, 400, 500, 600]);
        this._cardLink = this.closest(".product-card-simple__link") ?? null;
        this._originalHref = this._cardLink?.href ?? null;
      }

      this.initializeSelectedOptions();
      this.bindEvents();
    }

    /**
     * No preselecciona ninguna talla: el click en una talla es el add-to-cart, de modo
     * que preseleccionar equivaldria a elegir por el usuario la talla que va a comprar.
     * Los productos sin opcion de talla siguen resolviendo su variante por el input
     * hidden que Liquid renderizo con selected_or_first_available_variant, y conservan
     * su boton "Agregar a la bolsa".
     */
    initializeSelectedOptions() {
      this.selectedOptions = {};
      this.updateVariant();
    }

    bindEvents() {
      this.addEventListener("click", (e) => e.stopPropagation());

      this.addEventListener("click", (e) => {
        const btn = e.target.closest(".quick-add-custom__size-btn");
        if (btn) this.handleOptionClick(e, btn);
      });

      const colorsContainer = this.querySelector("[data-siblings-colors]");
      if (colorsContainer) {
        colorsContainer.addEventListener("mouseover", (e) => {
          const btn = e.target.closest(".quick-add-custom__color-btn");
          if (btn && btn.dataset.self !== "true" && btn.dataset.handle) {
            this.prefetchSiblingHandle(btn.dataset.handle);
          }
        });
        colorsContainer.addEventListener("click", (e) => {
          const btn = e.target.closest(".quick-add-custom__color-btn");
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          this.handleSiblingColorBtn(btn);
        });
        this.bindSiblingsIntent();
      }

      if (this.form) {
        this.form.addEventListener("submit", (e) => this.handleSubmit(e));
      }
    }

    /**
     * Defers the colour siblings request until the overlay can actually be seen.
     *
     * The panel is `opacity: 0; visibility: hidden; pointer-events: none` at rest and only reveals
     * on `.card-gallery:hover`, `.card-gallery:focus-within` or `.is-active` — and below 750px it is
     * `display: none !important`, so it never reveals at all. Loading on connect therefore issued
     * one Storefront GraphQL request per card for a panel nobody could look at: 64 requests spread
     * across 13 seconds on a mobile trace of the home, for markup that viewport cannot render.
     *
     * The listeners sit on the reveal root rather than on this element, because `pointer-events:
     * none` means the panel itself receives nothing until it is already visible. Listening on the
     * element too is the safety net for `.is-active`, which no script sets today but which the
     * stylesheet still honours.
     *
     * There are two reveal roots and the selector has to name both, because the stylesheets do:
     * `.card-gallery` for cards built by card-gallery.liquid, and `.product-card-simple__media`
     * for product-card-simple, which carries its own `:hover`/`:focus-within` rule and contains no
     * card-gallery at all. The home renders 32 of the simple variant against 4 of the other, so
     * matching only the first left the reveal root to `parentElement` on almost every card —
     * working by coincidence rather than by contract.
     */
    bindSiblingsIntent() {
      const revealRoot =
        this.closest(".card-gallery, .product-card-simple__media") || this.parentElement;

      const load = () => {
        if (this._siblingsRequested) return;
        const container = this.querySelector("[data-siblings-colors]");
        if (!container) return;
        this._siblingsRequested = true;
        this.loadQuickAddSiblings(container);
      };

      for (const target of new Set([revealRoot, this].filter(Boolean))) {
        target.addEventListener("pointerenter", load);
        target.addEventListener("focusin", load);
      }
    }

    /**
     * El click en una talla resuelve la variante y agrega al carrito en un solo gesto.
     *
     * No delega en un CTA aparte ni escribe su propia peticion: reutiliza handleSubmit(),
     * que es el mismo camino que usaba el boton "Agregar a la bolsa". Un solo add-to-cart
     * en el componente significa un solo lugar donde cambiar el feedback o arreglar un bug.
     *
     * Tampoco deja `is-selected` persistente. Sin preselección no hay seleccion que
     * sostener: la verdad de lo agregado vive en el carrito. El boton solo atraviesa su
     * ciclo is-loading -> is-added -> estado base.
     */
    handleOptionClick(event, btn) {
      event.preventDefault();
      event.stopPropagation();

      if (!btn) btn = event.currentTarget;
      if (btn.disabled) return;

      this.selectedOptions[btn.dataset.optionIndex] = btn.dataset.optionValue;
      const variant = this.updateVariant();
      this.updateSizeAvailability();

      // Solo agregamos si la seleccion resolvio una variante realmente comprable.
      if (variant?.available) this.handleSubmit(event, btn);
    }

    prefetchSiblingHandle(handle) {
      if (!handle || this._siblingCache.has(handle)) return;
      fetch("/products/" + handle + ".js")
        .then((r) => r.json())
        .then((data) => this._siblingCache.set(handle, data))
        .catch(() => {});
    }

    handleSiblingColorBtn(btn) {
      const colorsContainer = this.querySelector("[data-siblings-colors]");
      if (colorsContainer) {
        colorsContainer
          .querySelectorAll(".quick-add-custom__color-btn")
          .forEach((b) => b.classList.remove("is-selected"));
      }
      btn.classList.add("is-selected");

      if (btn.dataset.self === "true") {
        this.restoreOriginal();
        return;
      }

      this.selectSibling(btn.dataset.handle);
    }

    /**
     * Repinta la card con el producto hermano indicado, sin navegar.
     *
     * Es API publica y no toca el DOM de ningun boton: la usan los dos pickers de color que
     * conviven en la card — el del overlay de quick-add en desktop, y los swatches de
     * color-siblings en mobile, donde ese overlay esta oculto. Cada uno se ocupa de pintar su
     * propio estado seleccionado; el rebuild vive en un solo lugar.
     *
     * @param {string} handle - Handle del producto hermano.
     */
    selectSibling(handle) {
      if (!handle) return;

      if (this._siblingCache.has(handle)) {
        this.rebuildFromProduct(this._siblingCache.get(handle));
        return;
      }

      this.classList.add("is-loading");
      // Opcional: no se renderiza cuando las tallas son el CTA.
      if (this.submitBtn) this.submitBtn.disabled = true;

      const token = ++this._fetchToken;

      fetch("/products/" + handle + ".js")
        .then((r) => r.json())
        .then((data) => {
          if (token !== this._fetchToken) return;
          this._siblingCache.set(handle, data);
          this.rebuildFromProduct(data);
          this.classList.remove("is-loading");
        })
        .catch(() => {
          if (token !== this._fetchToken) return;
          this.classList.remove("is-loading");
          this.showFeedback("Error al cargar tallas");
        });
    }

    /**
     * Load sibling color buttons using the shared ColorSiblings service.
     * Falls back to a local predictive search if the service isn't available.
     */
    loadQuickAddSiblings(container) {
      const styleRef = container.dataset.styleRef;
      if (!styleRef) return;
      const currentHandle = container.dataset.currentHandle;

      // Use shared service (color-siblings.js) — single cache, no duplicate requests
      if (window.ColorSiblings) {
        window.ColorSiblings.fetch(styleRef).then((siblings) => {
          if (siblings)
            this.renderQuickAddSiblings(container, siblings, currentHandle);
        });
        return;
      }

      // Fallback: if color-siblings.js hasn't loaded yet (shouldn't happen normally)
      const params = [
        "q=" + encodeURIComponent(styleRef),
        "resources[type]=product",
        "resources[limit]=20",
        "resources[options][fields]=tag",
        "resources[options][unavailable_products]=show",
      ].join("&");

      fetch("/search/suggest.json?" + params)
        .then((r) => r.json())
        .then((json) => {
          const results =
            (json.resources &&
              json.resources.results &&
              json.resources.results.products) ||
            [];
          const erpPrefix = styleRef + "-";
          const styleRefLower = styleRef.toLowerCase();
          const siblings = results.filter((p) => {
            if (p.tags && p.tags.length)
              return p.tags.some((t) => t.indexOf(erpPrefix) === 0);
            return p.handle.indexOf(styleRefLower) !== -1;
          });
          this.renderQuickAddSiblings(container, siblings, currentHandle);
        })
        .catch(() => {});
    }

    renderQuickAddSiblings(container, siblings, currentHandle) {
      if (!siblings || siblings.length === 0) return;
      const otherSiblings = siblings.filter(
        (s) => s.handle !== currentHandle && s.available !== false,
      );
      if (otherSiblings.length === 0) return;

      const fragment = document.createDocumentFragment();

      otherSiblings.forEach((sibling) => {
        const colorName = window.ColorSiblings
          ? window.ColorSiblings.getColorName(sibling)
          : this._getSiblingColorName(sibling);

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "quick-add-custom__color-btn";
        btn.dataset.handle = sibling.handle;
        btn.setAttribute("aria-label", colorName);
        btn.setAttribute("title", colorName);

        const imgObj = sibling.featured_image || sibling.image;
        const imgUrl = imgObj && imgObj.url;
        if (imgUrl) {
          const optimizedUrl = imgUrl + (imgUrl.indexOf("?") !== -1 ? "&" : "?") + "width=80";
          btn.style.backgroundImage = "url(" + optimizedUrl + ")";
          btn.style.backgroundSize = "var(--color-picker-photo-zoom, 600%)";
          btn.style.backgroundPosition = "center 40%";
        }

        const span = document.createElement("span");
        span.className = "visually-hidden";
        span.textContent = colorName;
        btn.appendChild(span);

        fragment.appendChild(btn);
      });

      container.appendChild(fragment);
    }

    /** Fallback color name extraction when ColorSiblings service is unavailable */
    _getSiblingColorName(product) {
      if (product.variants && product.variants.length > 0) {
        const variantTitle = product.variants[0].title;
        if (variantTitle && variantTitle !== "Default Title") {
          const parts = variantTitle.split(" / ");
          return parts[parts.length - 1];
        }
      }
      return product.title;
    }

    restoreOriginal() {
      this.variants = [...this._originalVariants];
      this.dataset.productId = this._originalProductId;

      const sizeContainer = this.querySelector(".quick-add-custom__sizes");
      if (sizeContainer && this._originalSizesHTML !== null) {
        sizeContainer.innerHTML = this._originalSizesHTML;
      }

      // Volver al color propio devuelve el markup original de tallas, que no trae
      // ninguna seleccionada. El usuario vuelve a elegir talla, igual que al cargar.
      this.selectedOptions = {};
      this.updateVariant();

      const colorsContainer = this.querySelector("[data-siblings-colors]");
      if (colorsContainer) {
        colorsContainer
          .querySelectorAll(".quick-add-custom__color-btn")
          .forEach((b) => b.classList.remove("is-selected"));
        const selfBtn = colorsContainer.querySelector('[data-self="true"]');
        if (selfBtn) selfBtn.classList.add("is-selected");
      }

      if (this._mode === "slideshow") {
        if (this._slideshowScroller && this._originalScrollerHTML !== null) {
          this._slideshowScroller.innerHTML = this._originalScrollerHTML;
          this._resetSlideshowToFirst();
        }
      } else {
        if (this._cardImg && this._originalImageSrc) {
          this._cardImg.src = this._originalImageSrc;
          this._cardImg.srcset = this._originalImageSrcset ?? "";
        }
      }
      if (this._cardLink && this._originalHref)
        this._cardLink.href = this._originalHref;
    }

    rebuildFromProduct(productData) {
      const sizeContainer = this.querySelector(".quick-add-custom__sizes");
      if (!sizeContainer) return;

      const sizeIndex = parseInt(sizeContainer.dataset.optionIndex);
      const sizeOption = productData.options[sizeIndex];
      if (!sizeOption) return;

      this.variants = productData.variants.map((v) => ({
        id: v.id,
        available: v.available,
        options: v.options,
        title: v.title,
        price: v.price,
      }));

      this.dataset.productId = productData.id;

      const isSizeAvailable = (value) =>
        productData.variants.some(
          (v) => v.options[sizeIndex] === value && v.available,
        );

      // Mismo markup que renderiza Liquid, incluido el no preseleccionar: cambiar de
      // color deja al usuario eligiendo talla de nuevo, porque cada talla es un
      // add-to-cart y ninguna debe quedar armada sin que la haya tocado.
      sizeContainer.innerHTML = sizeOption.values
        .map((value) => {
          const available = isSizeAvailable(value);
          const escapedValue = escapeAttr(value);
          const label = available
            ? `Agregar talla ${value} a la bolsa`
            : `Talla ${value} agotada`;
          return `<button
          type="button"
          class="quick-add-custom__size-btn${!available ? " is-unavailable" : ""}"
          data-option-value="${escapedValue}"
          data-option-index="${sizeIndex}"
          ${!available ? "disabled" : ""}
          aria-label="${escapeAttr(label)}"
        >${escapedValue}</button>`;
        })
        .join("");

      this.selectedOptions = {};
      this.updateVariant();
      this.updateCardImage(productData);
      if (this._cardLink)
        this._cardLink.href = "/products/" + productData.handle;
    }

    updateCardImage(productData) {
      if (this._mode === "slideshow") {
        this._rebuildSlideshow(productData.images ?? []);
      } else {
        if (!this._cardImg) return;
        const rawUrl = productData.featured_image;
        if (!rawUrl) return;
        const base = rawUrl.split("?")[0];
        const url = base.startsWith("//") ? "https:" + base : base;
        const { widths } = this._cardImageTemplate;
        this._cardImg.src = `${url}?width=${widths[widths.length - 1]}`;
        this._cardImg.srcset = widths.map((w) => `${url}?width=${w} ${w}w`).join(", ");
      }
    }

    _rebuildSlideshow(images) {
      if (!this._slideshowScroller || !images.length) return;

      const fragment = document.createDocumentFragment();
      images.slice(0, 5).forEach((rawUrl, index) => {
        const base = rawUrl.split("?")[0];
        const url = base.startsWith("//") ? "https:" + base : base;

        const slide = document.createElement("slideshow-slide");
        slide.setAttribute("ref", "slides[]");
        slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");
        slide.setAttribute("slide-id", `sibling-${index}`);
        slide.className =
          "product-media-container media-fit product-media-container--image";

        const { widths, sizes, width, height } = this._slideImageTemplate;
        const img = document.createElement("img");
        img.src = `${url}?width=${widths[widths.length - 1]}`;
        img.srcset = widths.map((w) => `${url}?width=${w} ${w}w`).join(", ");
        // `sizes="auto"` is only valid on lazy images, and the first slide has to load immediately.
        if (sizes) img.sizes = index === 0 ? sizes.replace(/^auto,\s*/, "") : sizes;
        if (width) img.width = Number(width);
        if (height) img.height = Number(height);
        img.loading = index === 0 ? "eager" : "lazy";
        img.className = "product-media";
        img.alt = "";

        slide.appendChild(img);
        fragment.appendChild(slide);
      });

      this._slideshowScroller.innerHTML = "";
      this._slideshowScroller.appendChild(fragment);
      this._resetSlideshowToFirst();
    }

    _resetSlideshowToFirst() {
      if (!this._slideshow) return;
      requestAnimationFrame(() => {
        try {
          this._slideshow.select(0, null, { animate: false });
        } catch (e) {}
      });
    }

    /**
     * Resuelve la variante segun las opciones elegidas y devuelve la que matcheo (o
     * undefined). El retorno es lo que usa handleOptionClick para decidir si agrega.
     *
     * submitBtn y btnText son opcionales: cuando la card tiene selector de tallas el
     * boton no se renderiza, porque las tallas son el CTA. Sin estos guards el
     * componente tiraba TypeError al conectarse en cada card de la grilla.
     */
    updateVariant() {
      const matchingVariant = this.variants.find((variant) => {
        return Object.keys(this.selectedOptions).every((index) => {
          return (
            variant.options[parseInt(index)] === this.selectedOptions[index]
          );
        });
      });

      if (matchingVariant) {
        this.variantInput.value = matchingVariant.id;
        this.classList.remove("needs-selection");
        if (this.submitBtn) this.submitBtn.disabled = !matchingVariant.available;
        if (this.btnText) {
          this.btnText.textContent = matchingVariant.available
            ? "Agregar a la bolsa"
            : "Agotado";
        }
      } else {
        this.classList.add("needs-selection");
      }

      return matchingVariant;
    }

    updateSizeAvailability() {
      const sizeContainer = this.querySelector(".quick-add-custom__sizes");
      if (!sizeContainer) return;

      const sizeIndex = parseInt(sizeContainer.dataset.optionIndex);

      sizeContainer
        .querySelectorAll(".quick-add-custom__size-btn")
        .forEach((btn) => {
          const sizeValue = btn.dataset.optionValue;
          const isAvailable = this.variants.some(
            (v) => v.options[sizeIndex] === sizeValue && v.available,
          );
          btn.classList.toggle("is-unavailable", !isAvailable);
          btn.disabled = !isAvailable;
        });
    }

    /**
     * Unico add-to-cart del componente. Lo invocan el submit del boton (productos sin
     * tallas) y el click en una talla, que pasa ese boton como sourceBtn para que el
     * ciclo "Agregando..." -> "Agregado" se pinte donde el usuario hizo click.
     *
     * @param {Event} event
     * @param {HTMLButtonElement} [sourceBtn] - Boton de talla que origino el add.
     */
    async handleSubmit(event, sourceBtn) {
      event.preventDefault();
      event.stopPropagation();

      // El boton visible antes frenaba el doble envio con su propio `disabled`. Cuando
      // las tallas son el CTA ese freno no existe, y dos clicks seguidos sobre la misma
      // talla agregaban dos unidades.
      if (this._submitting) return;

      if (!this.variantInput.value || this.submitBtn?.disabled) {
        this.showFeedback("Selecciona una talla");
        return;
      }

      this._submitting = true;

      // Cuando el add viene de una talla, el estado vive en ese boton; si no, en el host
      // y su texto, como antes.
      const stateEl = sourceBtn ?? this;
      const textEl = sourceBtn ? null : this.btnText;
      const originalText = textEl?.textContent ?? null;

      stateEl.classList.add("is-loading");
      if (textEl) textEl.textContent = "Agregando...";

      const restore = () => {
        stateEl.classList.remove("is-loading", "is-added");
        if (textEl && originalText !== null) textEl.textContent = originalText;
      };

      // Dos adds seguidos sobre el mismo boton dejaban dos restores encolados, y el del
      // primer click borraba el "Agregado" que acababa de poner el segundo.
      clearTimeout(stateEl._restoreTimer);

      try {
        const formData = new FormData(this.form);

        // H-4.1.4 (H-035): se piden las secciones del carrito en la misma llamada del add.
        // Los componentes que pintan el carrito (cart-icon, cart-drawer, cart-items-component,
        // header-actions) esperan recibir el HTML ya renderizado por esta via; sin `sections`
        // la respuesta no lo trae y no tienen con que actualizarse.
        const sectionsToUpdate = new Set();
        document.querySelectorAll("cart-items-component").forEach((el) => {
          if (el instanceof HTMLElement && el.dataset.sectionId) {
            sectionsToUpdate.add(el.dataset.sectionId);
          }
        });
        if (sectionsToUpdate.size > 0) {
          formData.append("sections", Array.from(sectionsToUpdate).join(","));
          formData.append("sections_url", window.location.pathname);
        }

        const response = await fetch(
          window.Shopify?.routes?.root + "cart/add.js",
          {
            method: "POST",
            body: formData,
          },
        );

        const result = await response.json();

        if (response.ok) {
          stateEl.classList.remove("is-loading");
          stateEl.classList.add("is-added");
          if (textEl) textEl.textContent = "Agregado";

          // Antes del await de cart.js a proposito: la bolita sale apenas el add esta
          // confirmado, y sus 600ms de vuelo cubren el fetch. Asi el contador del header
          // termina de subir justo cuando la bolita aterriza sobre el icono.
          this._animateFlyToCart(stateEl);

          const cartResponse = await fetch(
            window.Shopify?.routes?.root + "cart.js",
          );
          const cart = await cartResponse.json();

          // H-4.1.4 (H-035): evento ESTANDAR de Shopify, el que si escuchan los componentes.
          //
          // Un tramo anterior migro todos los listeners del carrito a
          // `StandardEvents.cartLinesUpdate` (de `@shopify/events`) — ver el comentario en
          // quick-add.js:509. Pero este emisor, propio de Esprit, se quedo en el `cart:update`
          // de mas abajo, que desde entonces no escucha nadie: por eso agregar desde la PLP
          // no refrescaba ni el icono ni el drawer. En `origin/main` los listeners siguen en
          // `ThemeEvents.cartUpdate` y por eso alli si funciona.
          //
          // Se construye a mano porque este archivo es un script PLANO (lo inyecta
          // layout/theme.liquid:499, sin `type="module"`), asi que no puede importar el modulo.
          // El nombre se verifico contra el propio modulo del CDN
          // (https://cdn.shopify.com/storefront/standard-events.js): prefijo `shopify:` mas
          // `cart:lines-update`.
          //
          // El listener (component-cart-items.js:375) lee `event.promise` y espera que resuelva
          // con `{ detail: { sections } }`, de ahi la forma de abajo.
          const standardEvent = new CustomEvent("shopify:cart:lines-update", {
            bubbles: true,
          });
          standardEvent.promise = Promise.resolve({
            cart,
            detail: {
              source: "quick-add-custom",
              sections: result.sections,
              didError: false,
            },
          });
          document.dispatchEvent(standardEvent);

          // Evento legado de Esprit. Se CONSERVA: lo consumen otras piezas propias
          // (cart-cross-sell, scarab-cart-permalink, plp-variants).
          document.dispatchEvent(
            new CustomEvent("cart:update", {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: "quick-add-custom",
                data: {
                  source: "quick-add-custom",
                  itemCount: cart.item_count,
                  variantId: String(this.variantInput.value),
                  productId: this.dataset.productId,
                },
              },
            }),
          );

          stateEl._restoreTimer = setTimeout(restore, 2000);
        } else {
          restore();
          this.showFeedback(result.message || "Error al agregar");
        }
      } catch (error) {
        console.error("Add to cart error:", error);
        restore();
        this.showFeedback("Error de conexión");
      } finally {
        this._submitting = false;
      }
    }

    /**
     * Imagen que viaja al carrito: la que la card esta mostrando en ese momento, no la del
     * producto original. Si el usuario cambio de color con los siblings, la card ya repinto
     * su imagen y esa es la prenda que esta comprando.
     *
     * currentSrc antes que src porque las imagenes de card son responsive: currentSrc es el
     * candidato que el navegador realmente eligio del srcset, o sea el que ya esta en cache.
     */
    _getCardImageUrl() {
      const img =
        this._mode === "slideshow"
          ? this._slideshowScroller?.querySelector("img")
          : this._cardImg;

      return img?.currentSrc || img?.src || null;
    }

    /**
     * Anima una bolita con la imagen del producto desde el boton que el usuario toco hasta
     * el icono de bolsa del header.
     *
     * Reutiliza <fly-to-cart>, el custom element del vendor (assets/fly-to-cart.js), en vez
     * de escribir una animacion propia. Ese archivo es identico al de Horizon 4.1.3 y no
     * cambio entre 3.5.1 y 4.1.3, asi que la actualizacion de Horizon no lo toca y esta PLP
     * sigue funcionando despues del upgrade sin intervencion.
     *
     * Degrada en silencio en cada guard. Para cuando se llama, el producto YA esta en el
     * carrito y el contador del header ya va a subir: perder la animacion no le quita al
     * usuario ninguna confirmacion, solo el adorno. Por eso ningun guard avisa nada.
     *
     * @param {HTMLElement} sourceEl - Boton de talla que origino el add, o el host.
     */
    _animateFlyToCart(sourceEl) {
      // No esta registrado en template product ni en el editor: ahi product-form.js define
      // su propia version de <fly-to-cart>, incompatible con este CSS. Ver layout/theme.liquid.
      if (!customElements.get("fly-to-cart")) return;

      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

      const destination = document.querySelector(".header-actions__cart-icon");
      const image = this._getCardImageUrl();
      if (!destination || !sourceEl || !image) return;

      const fly = document.createElement("fly-to-cart");
      fly.classList.add("fly-to-cart--plp");
      fly.style.setProperty("background-image", `url(${image})`);
      fly.style.setProperty("--start-opacity", "0");

      // source y destination se leen en connectedCallback, asi que van antes del append.
      fly.source = sourceEl;
      fly.destination = destination;

      document.body.appendChild(fly);
    }

    showFeedback(message) {
      if (this.feedback && this.feedbackText) {
        this.feedbackText.textContent = message;
        this.feedback.hidden = false;
        setTimeout(() => {
          this.feedback.hidden = true;
        }, 3000);
      }
    }
  }

  customElements.define("quick-add-custom-component", QuickAddCustomComponent);
}
